import React, { useEffect, useState } from "react";
import { ROLL_CALL_API_BASE_URL } from "../../RollCallConfig/RollCallConfig";
import Swal from "sweetalert2";
import PropTypes from "prop-types";
import { Spin } from "antd";

const AddHoliday = ({ fetchdata, onClose, isOpen }) => {
  const [formData, setFormData] = useState({
    Reason: "",
    Date: "",
    Optional: false,
    IsYearly: false,
  });

  const [submitLoading, setSubmitLoading] = useState(false);
  const [tokenData, setTokenData] = useState(null);
  const [superIdData, setSuperIdData] = useState(null);
  const [repeatMonthly, setRepeatMonthly] = useState(false);
  const [selectedDay, setSelectedDay] = useState("");
  const [selectedWeek, setSelectedWeek] = useState("");
  const [datesList, setDatesList] = useState([]);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [excelDownloading, setExcelDownloading] = useState(false);
  const [file, setFile] = useState(null);
  const [excelUploading, setExcelUpLoading] = useState(false);

  const weekdays = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const weekOptions = [
    { label: "1st", value: 1 },
    { label: "2nd", value: 2 },
    { label: "3rd", value: 3 },
    { label: "4th", value: 4 },
    { label: "5th", value: 5 },
  ];

  const getNthWeekdayOfMonth = (year, month, weekday, nth) => {
    let date = new Date(year, month, 1);
    let count = 0;

    while (date.getMonth() === month) {
      if (date.getDay() === weekday) {
        count++;
        if (count === nth) {
          return new Date(date);
        }
      }
      date.setDate(date.getDate() + 1);
    }

    return null;
  };

  const generateDates = () => {
    if (!repeatMonthly || !selectedDay || !selectedWeek) {
      setDatesList([]);
      return;
    }

    const today = new Date();

    const endDate = new Date(today.getFullYear(), 11, 31);

    const weekdayIndex = weekdays.indexOf(selectedDay);
    const results = [];

    let tempDate = new Date(today.getFullYear(), today.getMonth(), 1);

    while (tempDate <= endDate) {
      const year = tempDate.getFullYear();
      const month = tempDate.getMonth();

      const date = getNthWeekdayOfMonth(
        year,
        month,
        weekdayIndex,
        selectedWeek,
      );

      if (date && date >= today && date <= endDate) {
        results.push(date);
      }

      tempDate.setMonth(tempDate.getMonth() + 1);
    }

    results.sort((a, b) => a - b);

    const formatDate = (date) => {
      const dd = String(date.getDate()).padStart(2, "0");
      const mm = String(date.getMonth() + 1).padStart(2, "0");
      const yyyy = date.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    };

    const formatted = results.map((d) => formatDate(d));

    console.log("Generated Dates:", formatted);
    setDatesList(formatted);
  };

  useEffect(() => {
    generateDates();
  }, [repeatMonthly, selectedDay, selectedWeek]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    const superId = sessionStorage.getItem("superId");

    if (token && superId) {
      setTokenData(token);
      setSuperIdData(superId);
    } else {
      console.warn("User data not found in sessionStorage");
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const formatDateForAPI = (dateStr) => {
    const [dd, mm, yyyy] = dateStr.split("-");
    return `${yyyy}-${mm}-${dd}`;
  };

  const resetForm = () => {
    setFormData({
      Reason: "",
      Date: "",
      Optional: false,
    });

    setDatesList([]);
    setFile(null);
    fetchdata();
    handlecancel();
    onClose();
  };

  const handleAddHoliday = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    if (!tokenData || !superIdData) return;
    const userData = JSON.parse(sessionStorage.getItem("userData"));

    try {
      let payload = [];

      if (repeatMonthly && datesList.length > 0) {
        payload = datesList.map((date) => ({
          SuperId: superIdData,
          HolidayDt: formatDateForAPI(date),
          CreatedBy: userData?.UserId,
          Reason: formData.Reason,
          Optional: formData.Optional ? 1 : 0,
          IsYearly: 0,
        }));
      } else {
        payload = [
          {
            SuperId: superIdData,
            HolidayDt: formData.Date,
            CreatedBy: userData?.UserId,
            Reason: formData.Reason,
            Optional: formData.Optional ? 1 : 0,
            IsYearly: formData.IsYearly ? 1 : 0,
          },
        ];
      }

      console.log("Final Payload:", payload);

      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}portal/holidays/createHolidays`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokenData}`,
          },
          body: JSON.stringify(payload),
        },
      );

      const responseData = await response.json();

      if (Array.isArray(responseData)) {
        let successCount = 0;
        let failedList = [];

        responseData.forEach((item) => {
          if (item.status) {
            successCount++;
          } else {
            const errorData = item.ResultData?.[0];
            failedList.push({
              date: errorData?.ExistingHolidayDate,
              message: errorData?.Message,
            });
          }
        });

        if (failedList.length === 0) {
          Swal.fire({
            title: "Success",
            text: `${successCount} holiday(s) added successfully`,
            icon: "success",
          }).then(() => {
            resetForm();
          });
        } else if (successCount > 0) {
          const errorDates = failedList.map((f) => f.date).join(", ");
          const errorMessage = failedList[0]?.message || "Failed";

          Swal.fire({
            title: "Partial Success",
            html: `
      <p><b>${successCount}</b> holiday(s) added successfully</p>
      <p><b>Failed:</b><br/>${errorDates} → ${errorMessage}</p>
    `,
            icon: "warning",
          }).then(() => {
            resetForm();
          });
        } else {
          const errorDates = failedList.map((f) => f.date).join(", ");
          const errorMessage = failedList[0]?.message || "Failed";

          const finalErrorText = `${errorDates} → ${errorMessage}`;
          Swal.fire({
            title: "Error",
            text: finalErrorText || "All inserts failed",
            icon: "error",
          });
        }
      } else {
        if (responseData.status) {
          Swal.fire({
            title: "Success",
            text: "Holiday added successfully",
            icon: "success",
          }).then(() => {
            resetForm();
          });
        } else {
          Swal.fire({
            title: "Error",
            text:
              responseData?.ResultData?.[0]?.Message || "Failed to add holiday",
            icon: "error",
          });
        }
      }
    } catch (error) {
      console.error("Error adding holiday:", error.message);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handlecancel = () => {
    setFormData({
      Reason: "",
      Date: "",
      Optional: false,
    });
  };

  useEffect(() => {
    const el = document.getElementById("offcanvasRightAddHoliday");
    if (!el || !window.bootstrap) return;

    const instance = window.bootstrap.Offcanvas.getOrCreateInstance(el);

    const handleClose = () => {
      setFormData({
        Reason: "",
        Date: "",
        Optional: false,
      });
    };

    instance._element.addEventListener("hidden.bs.offcanvas", handleClose);

    return () => {
      instance._element.removeEventListener("hidden.bs.offcanvas", handleClose);
    };
  }, []);

  const handleExcelDownload = async () => {
    try {
      setExcelDownloading(true);

      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}/portal/holidays/downloadHolidayTemplate`,
        {
          method: "GET",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to download file");
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      a.download = "holiday_template.xlsx";

      document.body.appendChild(a);
      a.click();
      a.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      alert("Download failed");
    } finally {
      setExcelDownloading(false);
    }
  };

  const handleExcelUpload = async () => {
    if (!file) {
      Swal.fire({
        title: "Warning",
        text: "Please select an Excel file",
        icon: "warning",
      });
      return;
    }

    try {
      setExcelUpLoading(true);

      const userData = JSON.parse(sessionStorage.getItem("userData"));

      const formData = new FormData();
      formData.append("file", file);
      formData.append("SuperId", superIdData);
      formData.append("createdby", userData?.UserId);

      const response = await fetch(
        `${ROLL_CALL_API_BASE_URL}/portal/holidays/uploadBulkHoldays`,
        {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${tokenData}`,
          },
        },
      );

      const data = await response.json();

      console.log("Upload Response:", data);

      if (!response.ok || !data.status) {
        throw new Error(data.message || "Upload failed");
      }

      const formatDate = (iso) => {
        const d = new Date(iso);
        const dd = String(d.getDate()).padStart(2, "0");
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const yyyy = d.getFullYear();
        return `${dd}-${mm}-${yyyy}`;
      };

      const dates = data.insertedRows.map((row) => formatDate(row.HolidayDt));

      const dateList = dates.join(", ");

      Swal.fire({
        title: "Upload Successful",
        html: `
        <p><b>${data.total}</b> holiday(s) inserted</p>
        <p style="text-align:left;"><b>Dates:</b><br/>${dateList}</p>
      `,
        icon: "success",
      }).then(() => {
        resetForm();
      });

      setFile(null);
    } catch (error) {
      console.error(error);

      Swal.fire({
        title: "Error",
        text: error.message || "Upload failed",
        icon: "error",
      });
    } finally {
      setExcelUpLoading(false);
    }
  };
  const handleClose = () => {
    setFormData({
      Reason: "",
      Date: "",
      Optional: false,
    });

    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData({
        Reason: "",
        Date: "",
        Optional: false,
      });
      setRepeatMonthly(false);
      setSelectedDay("");
      setSelectedWeek("");
      setDatesList([]);
      setFile(null);
    }
  }, [isOpen]);

  useEffect(() => {
    setFormData({
      Reason: "",
      Date: "",
      Optional: false,
    });
    setRepeatMonthly(false);
    setSelectedDay("");
    setSelectedWeek("");
    setDatesList([]);
    setFile(null);
  }, [isOpen, showBulkUpload]);
  return (
    <>
      {isOpen && (
        <div>
          <style>
            {`
        @media (max-width: 576px) {
          #offcanvasRightAddHoliday {
            width: 90% !important;
          }
        }

        @media (min-width: 577px) and (max-width: 992px) {
          #offcanvasRightAddHoliday {
            width: 55% !important;
          }
        }

        @media (min-width: 993px) and (max-width: 1440px) {
          #offcanvasRightAddHoliday {
            width: 32% !important;
          }
        }
          
       @media (min-width: 1441px) and (max-width:2550px) {
          #offcanvasRightAddHoliday {
            width: 25% !important;
          }
        }
        @media (min-width:2551px) {
          #offcanvasRightAddHoliday {
            width:18% !important;
          }
        }
        `}
          </style>
          {submitLoading && (
            <div
              className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
              style={{
                zIndex: 1050,
                backdropFilter: "blur(4px)",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              }}
            >
              <Spin size="large" tip="Processing..." />
            </div>
          )}

          <div className="offcanvas-backdrop fade show" onClick={onClose} />
          <div
            className="offcanvas offcanvas-end show pb-0"
            id="offcanvasRightAddHoliday"
            style={{ visibility: "visible" }}
          >
            <div className="offcanvas-header d-flex justify-content-between align-items-center">
              <h4 className="mb-0">Add Holiday</h4>

              <div className="d-flex align-items-center gap-2">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => setShowBulkUpload(!showBulkUpload)}
                >
                  {showBulkUpload ? "Single" : "Bulk"}
                  <i className="fas fa-upload ms-2"></i>
                </button>

                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    handlecancel();
                    handleClose();
                  }}
                  aria-label="Close"
                />
              </div>
            </div>
            <hr className="m-0" />
            <div className="offcanvas-body">
              {!showBulkUpload ? (
                <form onSubmit={handleAddHoliday}>
                  <div className="content">
                    <div className="row">
                      <div className="mb-3 col-12">
                        <label className="form-label">
                          <span className="text-danger ms-1">*</span>Reason
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter reason"
                          name="Reason"
                          value={formData.Reason}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      {!repeatMonthly && (
                        <div className="col-6">
                          <label className="form-label">
                            <span className="text-danger ms-1">*</span>Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            name="Date"
                            value={formData.Date}
                            onChange={handleInputChange}
                            required={!repeatMonthly}
                          />
                        </div>
                      )}

                      <div className="col-4 ms-2 my-3 form-check">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          name="Optional"
                          checked={formData.Optional}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              Optional: e.target.checked,
                            })
                          }
                        />
                        <label className="form-check-label">Optional</label>
                      </div>
                      <div className="d-none">
                        <div className="col-6 ms-2 my-3 form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={repeatMonthly}
                            onChange={() => setRepeatMonthly(!repeatMonthly)}
                          />
                          <label className="form-check-label">
                            Repeat Monthly
                          </label>
                        </div>
                      </div>

                      {!repeatMonthly && (
                        <div className="col-4 ms-2 my-3 form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name="IsYearly"
                            checked={formData.IsYearly}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                IsYearly: e.target.checked,
                              })
                            }
                          />
                          <label className="form-check-label">
                            Repeat Yearly
                          </label>
                        </div>
                      )}

                      {repeatMonthly && (
                        <>
                          <div className="col-6 mb-3 my-3 ">
                            <label className="form-label">Weekday</label>
                            <select
                              className="form-select"
                              value={selectedDay}
                              onChange={(e) => setSelectedDay(e.target.value)}
                            >
                              <option value="">Select Weekday</option>
                              {weekdays.map((day) => (
                                <option key={day} value={day}>
                                  {day}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="col-6 mb-3 my-3 ">
                            <label className="form-label">Select Week: </label>
                            <select
                              className="form-select"
                              value={selectedWeek}
                              onChange={(e) =>
                                setSelectedWeek(Number(e.target.value))
                              }
                            >
                              <option value="">Select Week</option>
                              {weekOptions.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                    </div>
                    {datesList.length > 0 && (
                      <div
                        className="mt-3 border p-2"
                        style={{ height: "180px", overflowY: "auto" }}
                      >
                        <div className="row g-2">
                          {datesList.map((date, index) => (
                            <div key={index} className="col-6">
                              <div className="p-2 border rounded text-center bg-light">
                                {date}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="row d-flex justify-content-end mt-3">
                      <div className="col-3 d-flex justify-content-end">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          data-bs-dismiss="offcanvas"
                          aria-label="Close"
                          onClick={() => {
                            handlecancel();
                            onClose();
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                      <div className="col-3 d-flex justify-content-end">
                        <button
                          type="submit"
                          className="btn btn-success"
                          disabled={submitLoading}
                        >
                          {submitLoading ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="content">
                  <div className="mt-3 border rounded p-3">
                    <h6 className="mb-3">
                      <i className="fas fa-file-excel me-2 text-success"></i>
                      Bulk Upload Holidays
                    </h6>

                    <div className="mb-3 text-muted">
                      Download the template, fill in data, then upload it.
                    </div>

                    <div className="mb-3">
                      <p className="mb-2">
                        You can download the template here:
                      </p>

                      <button
                        className="btn btn-success btn-sm"
                        onClick={handleExcelDownload}
                        disabled={excelDownloading}
                      >
                        {excelDownloading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Downloading...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-file-excel me-2"></i>
                            Download
                          </>
                        )}
                      </button>
                    </div>

                    <hr />

                    <div>
                      <p className="mb-2">Upload the completed Excel file:</p>

                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        className="form-control mb-2"
                        onChange={handleFileChange}
                      />

                      {file && (
                        <div className="mb-2 text-muted">
                          Selected: {file.name}
                        </div>
                      )}

                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleExcelUpload}
                        disabled={excelUploading}
                      >
                        {excelUploading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2"></span>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-cloud-upload-alt me-2"></i>
                            Upload
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

AddHoliday.propTypes = {
  fetchdata: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
};

export default AddHoliday;
